import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useCanvasStore } from '@/stores/canvasStore';
import { useDragToBoardStore } from '@/stores/dragToBoardStore';
import { usePartnrBetaStore } from '@/stores/partnrBetaStore';
import { ChevronRight, Home, Folder, Brain } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { BoardProfilePopover } from '@/components/partnr/BoardProfilePopover';

export function Breadcrumbs() {
  const { boardPath, navigateBack, currentBoardId, boards, moveItemsToBoard, updateBoard, goToFocusLanding } = useCanvasStore();
  const { isDragging, draggedItemIds } = useDragToBoardStore();
  const { isPartnrBetaEnabled } = usePartnrBetaStore();
  const [hoveredBreadcrumb, setHoveredBreadcrumb] = useState<string | null>(null);
  const [editingBoardId, setEditingBoardId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Board profile popover state
  const [showProfilePopover, setShowProfilePopover] = useState(false);
  const [profilePopoverPos, setProfilePopoverPos] = useState({ x: 0, y: 0 });
  const [profileBoardId, setProfileBoardId] = useState<string>('');
  const [profileBoardName, setProfileBoardName] = useState<string>('');
  
  // Get current board
  const currentBoard = boards.find(b => b.id === currentBoardId);
  
  // Get home board
  const homeBoard = boards.find(b => b.parent_id === null);

  // Focus input when editing starts
  useEffect(() => {
    if (editingBoardId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingBoardId]);

  const handleHomeClick = () => {
    goToFocusLanding();
  };

  const handleBreadcrumbClick = (boardId: string) => {
    navigateBack(boardId);
  };

  // Handle double-click to edit current board name
  const handleDoubleClick = useCallback((boardId: string, boardName: string) => {
    setEditingBoardId(boardId);
    setEditName(boardName);
  }, []);

  const handleSave = useCallback(async () => {
    if (!editingBoardId) return;
    
    const trimmedName = editName.trim();
    const board = boards.find(b => b.id === editingBoardId);
    
    if (trimmedName && trimmedName !== board?.name) {
      await updateBoard(editingBoardId, { name: trimmedName });
      toast.success('Board renamed');
    }
    
    setEditingBoardId(null);
    setEditName('');
  }, [editingBoardId, editName, boards, updateBoard]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setEditingBoardId(null);
      setEditName('');
    }
  }, [handleSave]);

  // Handle drop on breadcrumb
  const handleBreadcrumbDrop = async (targetBoardId: string | null) => {
    if (!isDragging || draggedItemIds.length === 0) return;
    
    const actualBoardId = targetBoardId ?? homeBoard?.id;
    if (!actualBoardId) return;
    
    // Don't drop on current board
    if (actualBoardId === currentBoardId) {
      toast.info('Items are already in this board');
      return;
    }
    
    await moveItemsToBoard([...draggedItemIds], actualBoardId);
    useDragToBoardStore.getState().reset();
  };

  const handleMouseEnter = (boardId: string | null) => {
    if (isDragging) {
      setHoveredBreadcrumb(boardId ?? 'home');
    }
  };

  const handleMouseLeave = () => {
    setHoveredBreadcrumb(null);
  };

  const handleMouseUp = (boardId: string | null) => {
    if (isDragging) {
      handleBreadcrumbDrop(boardId);
    }
  };

  const isDropTarget = isDragging && draggedItemIds.length > 0;

  const shouldShow = currentBoard && currentBoard.parent_id !== null;

  const openBoardProfile = (e: React.MouseEvent, boardId: string, boardName: string) => {
    // Position below the breadcrumb area (the nav bar), not the context menu item
    const breadcrumbNav = document.querySelector('nav.flex.items-center');
    const rect = breadcrumbNav?.getBoundingClientRect() ||
      (e.target as HTMLElement).getBoundingClientRect();

    setProfilePopoverPos({
      x: Math.max(16, rect.left),
      y: rect.bottom + 12
    });
    setProfileBoardId(boardId);
    setProfileBoardName(boardName);
    setShowProfilePopover(true);
  };

  return (
    <>
      <AnimatePresence>
        {shouldShow && (
          <motion.nav
            key="breadcrumbs"
            initial={{ opacity: 0, y: -12, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.95 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className="flex items-center gap-0.5 text-sm bg-background/80 backdrop-blur-md rounded-lg px-2 py-1.5 shadow-sm border border-border/50"
          >
            {/* Home breadcrumb with context menu */}
            <ContextMenu>
              <ContextMenuTrigger asChild>
                <button
                  className={cn(
                    'flex items-center gap-1.5 px-2 py-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-150',
                    isDropTarget && 'cursor-copy',
                    hoveredBreadcrumb === 'home' && isDropTarget && 
                      'ring-2 ring-primary bg-primary/10 text-primary'
                  )}
                  onClick={handleHomeClick}
                  onMouseEnter={() => handleMouseEnter(null)}
                  onMouseLeave={handleMouseLeave}
                  onMouseUp={() => handleMouseUp(null)}
                >
                  <Home className="h-3.5 w-3.5" />
                  <span className="text-[13px]">Home</span>
                  {hoveredBreadcrumb === 'home' && isDropTarget && (
                    <span className="text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full font-medium">
                      Drop
                    </span>
                  )}
                </button>
              </ContextMenuTrigger>
              {isPartnrBetaEnabled && (
                <ContextMenuContent>
                  <ContextMenuItem
                    onClick={(e) => openBoardProfile(e, homeBoard?.id || '', 'Home')}
                  >
                    <Brain className="mr-2 h-4 w-4 text-partnr" />
                    Board Profile
                  </ContextMenuItem>
                </ContextMenuContent>
              )}
            </ContextMenu>

            {/* Path breadcrumbs with context menus */}
            {boardPath.map((board) => (
              <React.Fragment key={board.id}>
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50 mx-0.5" />
                <ContextMenu>
                  <ContextMenuTrigger asChild>
                    <button
                      className={cn(
                        'flex items-center gap-1.5 px-2 py-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-150',
                        isDropTarget && 'cursor-copy',
                        hoveredBreadcrumb === board.id && isDropTarget && 
                          'ring-2 ring-primary bg-primary/10 text-primary'
                      )}
                      onClick={() => handleBreadcrumbClick(board.id)}
                      onMouseEnter={() => handleMouseEnter(board.id)}
                      onMouseLeave={handleMouseLeave}
                      onMouseUp={() => handleMouseUp(board.id)}
                    >
                      <Folder className="h-3.5 w-3.5" style={{ color: board.icon_color }} />
                      <span className="text-[13px]">{board.name}</span>
                      {hoveredBreadcrumb === board.id && isDropTarget && (
                        <span className="text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full font-medium">
                          Drop
                        </span>
                      )}
                    </button>
                  </ContextMenuTrigger>
                  {isPartnrBetaEnabled && (
                    <ContextMenuContent>
                      <ContextMenuItem
                        onClick={(e) => openBoardProfile(e, board.id, board.name)}
                      >
                        <Brain className="mr-2 h-4 w-4 text-partnr" />
                        Board Profile
                      </ContextMenuItem>
                    </ContextMenuContent>
                  )}
                </ContextMenu>
              </React.Fragment>
            ))}

            {/* Current board (editable on double-click) */}
            {currentBoard && currentBoard.parent_id !== null && !boardPath.find(b => b.id === currentBoard.id) && (
              <>
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50 mx-0.5" />
                {editingBoardId === currentBoard.id ? (
                  <Input
                    ref={inputRef}
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onBlur={handleSave}
                    onKeyDown={handleKeyDown}
                    className="h-6 px-2 py-0 text-[13px] font-medium w-[120px] min-w-0"
                  />
                ) : (
                  <ContextMenu>
                    <ContextMenuTrigger asChild>
                      <div 
                        className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-muted/50 cursor-text"
                        onDoubleClick={() => handleDoubleClick(currentBoard.id, currentBoard.name)}
                        title="Double-click to rename"
                      >
                        <Folder className="h-3.5 w-3.5" style={{ color: currentBoard.icon_color }} />
                        <span className="text-[13px] font-medium text-foreground">
                          {currentBoard.name}
                        </span>
                      </div>
                    </ContextMenuTrigger>
                    {isPartnrBetaEnabled && (
                      <ContextMenuContent>
                        <ContextMenuItem
                          onClick={(e) => openBoardProfile(e, currentBoard.id, currentBoard.name)}
                        >
                          <Brain className="mr-2 h-4 w-4 text-partnr" />
                          Board Profile
                        </ContextMenuItem>
                      </ContextMenuContent>
                    )}
                  </ContextMenu>
                )}
              </>
            )}
          </motion.nav>
        )}
      </AnimatePresence>

      {/* Board Profile Popover */}
      {showProfilePopover && (
        <BoardProfilePopover
          boardId={profileBoardId}
          boardName={profileBoardName}
          onClose={() => setShowProfilePopover(false)}
          position={profilePopoverPos}
        />
      )}
    </>
  );
}
